import React from "react";
import { useTranslation } from "react-i18next";
import { Box, Typography, Paper, Divider } from "@mui/material";
import { Info as InfoIcon } from "@mui/icons-material";
import { isNotNullOrEmptyArray } from "../../globals/utils";

const AmSoilMoistureGraphSummary = ({
  soilMoistureData,
  wiltingPoint,
  fieldCapacity,
}) => {
  const { t } = useTranslation();
  if (!isNotNullOrEmptyArray(soilMoistureData)) return null;

  const latestData = soilMoistureData[soilMoistureData.length - 1];
  const currentMoisture = latestData?.soil_moisture || 0;
  const averageMoisture =
    soilMoistureData.reduce((sum, item) => sum + item.soil_moisture, 0) /
    soilMoistureData.length;

  const getMoistureStatus = (moisture) => {
    if (moisture < wiltingPoint) return { status: "low", color: "#EB5353" };
    if (moisture > fieldCapacity) return { status: "high", color: "#FF9500" };
    return { status: "optimal", color: "#36AE7C" };
  };

  const currentStatus = getMoistureStatus(currentMoisture);
  const averageStatus = getMoistureStatus(averageMoisture);

  const getStatusText = (status) => {
    switch (status) {
      case "low":
        return t("Too Dry - Irrigation Needed");
      case "high":
        return t("Too Wet - Risk of Waterlogging");
      case "optimal":
        return t("Optimal Range");
      default:
        return "";
    }
  };

  const getRecommendation = (status) => {
    switch (status) {
      case "low":
        return t(
          "Consider irrigating your field to improve crop growth and yield"
        );
      case "high":
        return t("Ensure proper drainage to prevent root damage and disease");
      case "optimal":
        return t("Soil moisture is in the ideal range for healthy crop growth");
      default:
        return "";
    }
  };

  return (
    <Paper
      elevation={1}
      sx={{
        p: 3,
        mb: 3,
        borderLeft: `4px solid ${currentStatus.color}`,
        backgroundColor: "#f8f9fa",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <InfoIcon sx={{ color: "primary.main", mr: 1 }} />
        <Typography
          variant="h6"
          sx={{ fontWeight: "bold", color: "text.primary" }}
        >
          {t("Soil Moisture Summary")}
        </Typography>
      </Box>

      <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.6 }}>
        {t(
          "This graph shows how much water is available in your soil over time. Soil moisture is measured as a percentage, where higher values mean more water content."
        )}
      </Typography>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: 2,
          mb: 2,
        }}
      >
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1 }}>
            {t("Current Status")}
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: currentStatus.color, fontWeight: "bold" }}
          >
            {currentMoisture.toFixed(1)}% -{" "}
            {getStatusText(currentStatus.status)}
          </Typography>
        </Box>

        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1 }}>
            {t("Average Over Period")}
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: averageStatus.color, fontWeight: "bold" }}
          >
            {averageMoisture.toFixed(1)}% -{" "}
            {getStatusText(averageStatus.status)}
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ my: 2 }} />

      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1 }}>
          {t("Understanding the Reference Lines")}
        </Typography>
        <Box sx={{ ml: 1 }}>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            <span style={{ color: "#EB5353", fontWeight: "bold" }}>
              • {t("Red Line")} ({wiltingPoint}%)
            </span>
            :{" "}
            {t(
              "Minimum water needed - below this, plants start to wilt and stress"
            )}
          </Typography>
          <Typography variant="body2">
            <span style={{ color: "#36AE7C", fontWeight: "bold" }}>
              • {t("Green Line")} ({fieldCapacity}%)
            </span>
            :{" "}
            {t(
              "Maximum beneficial water - above this, excess water may cause problems"
            )}
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          p: 2,
          backgroundColor: "white",
          borderRadius: 1,
          border: `1px solid ${currentStatus.color}`,
          borderLeft: `4px solid ${currentStatus.color}`,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: "bold", mb: 1, color: currentStatus.color }}
        >
          {t("Recommendation")}
        </Typography>
        <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
          {getRecommendation(currentStatus.status)}
        </Typography>
      </Box>
    </Paper>
  );
};

export default AmSoilMoistureGraphSummary;
